import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem('jwttoken');
    return (t === 'undefined' || t === 'null') ? null : t;
  });
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(() => localStorage.getItem('authModule') || 'kaas');
  const [notifications, setNotifications] = useState([]);


  // Global interceptor for 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn("Unauthorized access detected (401). Current token:", token);
          if (token) {
            console.log("Forcing logout due to 401...");
            logout();
            const currentModule = localStorage.getItem('authModule') || 'kaas';
            if (currentModule === 'gatep') {
              if (!window.location.pathname.includes('/gatep/login')) {
                window.location.href = '/db-shiksha/gatep/login';
              }
            } else {
              if (!window.location.pathname.includes('/kaas/login')) {
                window.location.href = '/db-shiksha/kaas/login';
              }
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const fetchUserData = useCallback(async (currentToken) => {
    if (!currentToken || currentToken === 'undefined' || currentToken === 'null') return;
    try {
      const response = await axios.get('https://tdtlworld.com/dss-kaas-backend/lms/user/', {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setUserData(response.data);
    } catch (err) {
      console.error("AuthContext: Error fetching user data", err);
      // If we get a 401 here, it's a strong signal the token is dead
      if (err.response?.status === 401) {
        setToken(null);
        localStorage.removeItem('jwttoken');
      }
    }
  }, []);

  const fetchNotifications = useCallback(async (currentToken) => {
    if (!currentToken || module !== 'gatep') return;
    try {
      const response = await axios.get('https://tdtlworld.com/dss-gatep-backend/api/notifications/', {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      // Handle both { notifications: [] } and [] formats
      const data = response.data.notifications || response.data;
      if (Array.isArray(data)) {
        setNotifications(data);
      }
    } catch (err) {
      console.error("AuthContext: Error fetching notifications", err);
    }
  }, [module]);

  const updateNotification = useCallback(async (id, status, isRead = true) => {
    if (!token) return;
    try {
      await axios.patch(`https://tdtlworld.com/dss-gatep-backend/api/notifications/${id}/`,
        { status, is_read: isRead },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, status, is_read: isRead } : n)
      );
    } catch (err) {
      console.error("AuthContext: Error updating notification", err);
    }
  }, [token]);

  useEffect(() => {
    console.log("AuthContext: Token effect running. Token:", !!token);
    if (token) {
      try {
        const payloadBase64 = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payloadBase64));

        setUser({
          email: decodedPayload.email,
          role: localStorage.getItem('role')
        });

        console.log("AuthContext: Calling fetchUserData...");
        if (module === 'kaas') {
          fetchUserData(token);
        } else if (module === 'gatep') {
          fetchNotifications(token);
        }
      } catch (e) {

        console.error("Failed to decode token", e);
        logout();
      }
    } else {
      setUser(null);
      setUserData(null);
    }
    setLoading(false);
  }, [token, fetchUserData]);

  const login = (newToken, userRole, userEmail, userModule = 'kaas') => {
    localStorage.setItem('jwttoken', newToken);
    localStorage.setItem('role', userRole);
    localStorage.setItem('email', userEmail);
    localStorage.setItem('authModule', userModule);
    setToken(newToken);
    setModule(userModule);
    setUser({ email: userEmail, role: userRole });
    if (userModule === 'kaas') {
      fetchUserData(newToken);
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('https://tdtlworld.com/dss-gatep-backend/api/forgot-password/', { email });
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to send reset email.' };
    }
  };


  const logout = () => {
    localStorage.removeItem('jwttoken');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('password');
    localStorage.removeItem('authModule');
    // GateP specific
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('gatep_user');

    setToken(null);
    setUser(null);
    setUserData(null);
    setModule('kaas');

    // Force redirect to KaaS login
    window.location.href = "/db-shiksha/kaas/login";
  };



  const refreshUserData = useCallback(() => {
    if (token) fetchUserData(token);
  }, [token, fetchUserData]);

  const contextValue = useMemo(() => ({
    user,
    userData,
    token,
    loading,
    module,
    login,
    logout,
    forgotPassword,
    refreshUserData,
    notifications,
    updateNotification
  }), [user, userData, token, loading, module, login, logout, forgotPassword, refreshUserData, notifications, updateNotification]);


  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
